import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebaseConfig";
import DownloadTab from "./DownloadTab";

/**
 * Super Admin > Download
 * Multi-org selector + per-store link/PIN generator (reuses DownloadTab).
 */
export default function SuperDownload() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoadingUser(false);
    });
    return () => unsub();
  }, []);

  const urlOrgId = String(searchParams.get("orgId") || "").trim();
  const storedOrgId = String(localStorage.getItem("superOrgId") || "").trim();
  const [orgId, setOrgId] = useState(urlOrgId || storedOrgId || "");

  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [orgErr, setOrgErr] = useState("");

  useEffect(() => {
    const next = urlOrgId || storedOrgId || "";
    setOrgId((prev) => (prev === next ? prev : next));
  }, [urlOrgId, storedOrgId]);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoadingOrgs(true);
      setOrgErr("");
      try {
        const q = query(collection(db, "orgs"), orderBy("name", "asc"));
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!alive) return;
        setOrgs(rows);
      } catch (e) {
        console.error("Failed to load orgs", e);
        if (!alive) return;
        setOrgs([]);
        setOrgErr(e?.message || "Failed to load organizations.");
      } finally {
        if (!alive) return;
        setLoadingOrgs(false);
      }
    }
    if (!loadingUser && user) load();
    if (!loadingUser && !user) {
      setLoadingOrgs(false);
      setOrgs([]);
    }
    return () => {
      alive = false;
    };
  }, [user, loadingUser]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);

    if (orgId) {
      localStorage.setItem("superOrgId", orgId);
      if (next.get("orgId") !== orgId) {
        next.set("orgId", orgId);
        setSearchParams(next, { replace: true });
      }
      return;
    }

    localStorage.removeItem("superOrgId");
    if (next.has("orgId")) {
      next.delete("orgId");
      setSearchParams(next, { replace: true });
    }
  }, [orgId, searchParams, setSearchParams]);

  const selectedOrg = useMemo(() => orgs.find((o) => o.id === orgId) || null, [orgs, orgId]);

  useEffect(() => {
    if (!loadingUser && !user) {
      navigate("/login", { replace: true });
    }
  }, [loadingUser, user, navigate]);

  if (loadingUser) return <div style={{ padding: 24, color: "#cbd5e1" }}>Loading…</div>;
  if (!user) return <div style={{ padding: 24, color: "#94a3b8" }}>Redirecting…</div>;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: "#e5e7eb" }}>Download / Install</h2>
        <div style={{ color: "#94a3b8", fontSize: 13 }}>
          Pick an org, then generate a per-store link + one-time PIN.
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          background: "#0b1220",
          border: "1px solid #1f2937",
          borderRadius: 10,
          padding: 12,
          marginBottom: 18,
        }}
      >
        <div style={{ color: "#94a3b8", fontSize: 13 }}>Organization</div>
        <select
          value={orgId}
          onChange={(e) => setOrgId(String(e.target.value || "").trim())}
          style={{
            minWidth: 260,
            padding: "10px 12px",
            background: "#0f172a",
            color: "#e5e7eb",
            border: "1px solid #334155",
            borderRadius: 8,
          }}
        >
          <option value="">{loadingOrgs ? "Loading orgs..." : "Select an organization..."}</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name || o.id}
            </option>
          ))}
        </select>

        <button
          onClick={() => navigate("/super/orgs")}
          style={{
            marginLeft: "auto",
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #334155",
            background: "#111827",
            color: "#e5e7eb",
            cursor: "pointer",
          }}
        >
          Manage orgs
        </button>
      </div>

      {orgErr ? (
        <div
          style={{
            marginBottom: 12,
            padding: 12,
            borderRadius: 10,
            border: "1px solid #7f1d1d",
            background: "rgba(127, 29, 29, 0.25)",
            color: "#fecaca",
          }}
        >
          {orgErr}
        </div>
      ) : null}

      {orgId ? <DownloadTab key={orgId || "no-org"} orgId={orgId} org={selectedOrg} /> : <div style={{ color: "#94a3b8" }}>Select an organization to continue.</div>}
    </div>
  );
}
